'use client';

import classes from "./registration_container.module.css";
import social_classes from "./social_buttons.module.css"
import SafeImage from "@/components/common/safe_image";
import waving_hand from '@/assets/png/waving-hand.png';
import mailbox from '@/assets/png/mailbox.png';
import SocialButtons from "./social_buttons";
import { useContext, useRef, useState, useEffect } from "react";
import TextInput from "../inputs/text_input";
import CustomButton from "../buttons/button";
import ArrowRight from '@/assets/svg/icons/arrow-right.svg';
import ChevronRight from '@/assets/svg/icons/chevron-right.svg';
import PasswordReset from "../password_change/password_change";
import { resendVerifyEmail, signUp } from "@/utils/http";
import { useMutation } from "@tanstack/react-query";
import AlertTriangle from '@/assets/svg/icons/alert-triangle.svg';
import Mail from '@/assets/svg/icons/mail.svg';
import X from '@/assets/svg/icons/x.svg';
import CustomSVG from "../svg";
import FormControl from "../checks_and_radios/form_control";
import Link from "next/link";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import AuthContext from "@/providers/auth-context";
import ToastContext from "@/providers/toast-context";
import useAnalyticsTracking from "@/components/common/analytics/use_analytics_tracking";
import LinkWithParams from "@/components/link_with_params/link_with_params";
import { useRouter } from "next/navigation";
import { SetErrorMsg } from "@/functions/set_error_msg";
import { useSession } from "next-auth/react";
import { getRecaptchaToken, needsEmailVerification, getRecaptchaScore, isRecaptchaError, getRecaptchaErrorMessage } from "@/utils/recaptcha";
import EmailVerificationModal from "../email_verification_modal/email_verification_modal";
import RecaptchaV2Modal from "../recaptcha_v2_modal/recaptcha_v2_modal";
import useOpenAiAdsPixel from "../analytics/use_openai_ads_pixel";

export default function RegistrationContainer({ success, setSucces, is_modal=false }) {
    const [email, setEmail] = useState("");
    const [isError, setIsError] = useState(false);
    const [errCode, setErrCode] = useState(0);
    const [expanded, setExpanded] = useState(false);
    const [aszf, setAszf] = useState(false);
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [verificationScore, setVerificationScore] = useState(null);
    const [showRecaptchaV2Modal, setShowRecaptchaV2Modal] = useState(false);
    const [pendingRegistrationData, setPendingRegistrationData] = useState(null);
    const passwordRef = useRef();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { setShowLogin, setShowLoginState, redirectURL, loginIsProvider, provLoginRegFn } = useContext(AuthContext);
    const toastCtx = useContext(ToastContext);
    const router = useRouter();
    const { data: session, status } = useSession();
    const { trackEvent: trackRegistrationEvent } = useAnalyticsTracking('registrationTracking');
    const { measure: measureOpenAiAds } = useOpenAiAdsPixel();

    const { mutate: signUpInternal, isPending: isSignUpPending } = useMutation({
        mutationFn: signUp,
        onSuccess: (response) => {
            if (response.success) {
                trackRegistrationEvent(
                    'user_registration_success',
                    {
                        registered_user_id: response.response?.id,
                        registration_method: 'email'
                    },
                    {
                        pushToDataLayer: true,
                        pushToGtag: false
                    }
                );
                measureOpenAiAds('registration_completed');

                // Ellenőrizzük, hogy kell-e email verification
                if (needsEmailVerification(response)) {
                    const score = getRecaptchaScore(response);
                    setVerificationScore(score);
                    setShowVerificationModal(true);
                } else {
                    setSucces(true);
                }
            } else {
                // Backend can also return business style {success:false, recaptcha_v2_required:true}
                // (even with HTTP 200 in some flows). In that case we must open the v2 challenge.
                if (response?.success === false && response?.recaptcha_v2_required) {
                    const passwords = passwordRef.current.validate();
                    if (passwords) {
                        setPendingRegistrationData({
                            email: email,
                            password: passwords.password,
                            repassword: passwords.repassword
                        });
                        setShowRecaptchaV2Modal(true);
                    }
                    return;
                }

                if (response.message === "A(z) 'email' változó értéke már létezik") {
                    setErrCode(1);
                    setIsError(true);
                } else if (response.message === "Az e-mail cím aktiválás alatt van") {
                    setErrCode(2);
                    setIsError(true);
                }
            }
        },
        onError: (error) => {
            console.error("Registration error:", error);
            
            // reCAPTCHA v2 fallback check
            // NOTE: axios rejects 403, so registration challenge request is expected to land here.
            if (error?.response?.data?.recaptcha_v2_required) {
                // Store registration data for retry
                const passwords = passwordRef.current.validate();
                if (passwords) {
                    setPendingRegistrationData({
                        email: email,
                        password: passwords.password,
                        repassword: passwords.repassword
                    });
                    setShowRecaptchaV2Modal(true);
                }
                return;
            }
            
            if (isRecaptchaError(error)) {
                setErrCode(999);
                setIsError(true);
                
                // Show error toast for v2 validation failure
                toastCtx.addToast({
                    type: "error",
                    header_text: "Regisztráció sikertelen",
                    sub_text: "Biztonsági ellenőrzés sikertelen. Kérjük, próbáld újra később!"
                });
            } else {
                setIsError(true);
                setErrCode(999); // Általános hiba
                
                // Show error toast for general error
                toastCtx.addToast({
                    type: "error",
                    header_text: "Regisztráció sikertelen",
                    sub_text: "Biztonsági ellenőrzés sikertelen. Kérjük, próbáld újra később!"
                });
            }
        }
    });

    // reCAPTCHA v2 modal handlers
    const handleV2Success = async (token) => {
        if (!pendingRegistrationData) return;

        // Retry registration:
        // - keep sending a v3 token in captcha_response (schema expects it, and it drives scoring/logging)
        // - send v2 token in recaptcha_v2_response
        // - add a bypass flag to avoid re-triggering v2 in schema after we already validated it in this request
        if (!executeRecaptcha) {
            console.log("not available to execute recaptcha")
            return;
        }

        const gRecaptchaToken = await getRecaptchaToken(executeRecaptcha, "registration");

        signUpInternal({
            params: {
                captcha_response: gRecaptchaToken,
                recaptcha_v2_response: token,
                recaptcha_v2_validated: true,
                email: pendingRegistrationData.email,
                password: pendingRegistrationData.password,
                repassword: pendingRegistrationData.repassword,
                gdpr: true
            }
        });

        setShowRecaptchaV2Modal(false);
        setPendingRegistrationData(null);
    };

    const handleV2Close = () => {
        setShowRecaptchaV2Modal(false);
        setPendingRegistrationData(null);
        // Form fields remain intact
    };

    const { mutate: resend, isPending: isResending } = useMutation({
        mutationFn: resendVerifyEmail,
        onSuccess: (response) => {
            if (response.success) {
                // Ha sikeres az újraküldés
                if (needsEmailVerification(response)) {
                    // Marad a modal, csak újraindul a countdown
                    const score = getRecaptchaScore(response);
                    setVerificationScore(score);
                } else {
                    setSucces(true);
                    setShowVerificationModal(false);
                }
            } else {
                if (response.message === "A felhasználó elérte a regisztrációs e-mail küldési limitet") {
                    setErrCode(3);
                    setIsError(true);
                    setShowVerificationModal(false);
                }
            }
        },
        onError: (error) => {
            console.error("Resend email error:", error);
            setShowVerificationModal(false);
        }
    });

    const handleEmailChange = (val) => {
        setEmail(val);
        setIsError(false);
        setErrCode(0);
    }

    const handleContinue = async (e) => {
        e.preventDefault();

        if (!expanded) {
            setExpanded(true);
            return false;
        } else {
            if (!executeRecaptcha) {
                console.log("not available to execute recaptcha")
                return;
            }

            if (!email.trim()) {
                setIsError("Kötelező mező");
                return false;
            }

            let passwords = passwordRef.current.validate();
            if (!passwords) return false;

            const gRecaptchaToken = await getRecaptchaToken(executeRecaptcha, "registration");

            signUpInternal({
                params: {
                    captcha_response: gRecaptchaToken,
                    email: email,
                    password: passwords.password,
                    repassword: passwords.repassword,
                    gdpr: true
                }
            })
        }
    }

    const resendEmail = async () => {
        if (!executeRecaptcha) {
            console.log("not available to execute recaptcha")
            return;
        }

        const gRecaptchaToken = await getRecaptchaToken(executeRecaptcha, "resend");

        resend({
            params: {
                email: email,
                captcha_response: gRecaptchaToken,
            }
        })
    }

    const loginRedirect = () => {
        if (is_modal) setShowLoginState("login")
        else router.push("/bejelentkezes");
    }

    let err_msg = SetErrorMsg(errCode);

    useEffect(() => {
        if (status === "authenticated" && session.user) {
            window.location.reload();
        }
    }, [session, status]);

    return <div className={`${classes["registration-container"]} ${is_modal ? classes["modal-container"] : ""} position-relative`} ><div className="d-flex flex-row align-items-center w-100 overflow-auto">
        {success
            ? <RegistrationResendEmail email={email} resendEmail={resendEmail} is_modal={is_modal} isResending={isResending} />
            : <div className={`${classes["registration-container-inner"]} ${loginIsProvider ? classes["provider"] : ""}`} style={{ margin: "auto" }} >
                <div className={`${classes["registration-container-header-texts"]}`}>
                    {loginIsProvider
                        ? <div className="d-flex flex-row align-items-center gap-1 align-self-stretch w-100">
                            <h1 className="margin-0 padding-0 flex-grow-1 color-gray-900 display-sm-bold">Regisztráció</h1>
                            <CustomSVG className={`stroke-gray-500 flex-shrink-0 pointer`} SvgFunction={X} height={24} width={24} onClick={() => setShowLogin(false)} />
                        </div>
                        : <h1 className={`${classes["registration-container-header-texts-inner"]} color-gray-900 display-md-bold`} >
                            Üdvözlünk!
                            <SafeImage src={waving_hand} width={32} height={32} priority alt="integető kéz" />
                            {is_modal && <CustomSVG className={`stroke-gray-500 ${classes["registration-container-x"]}`} SvgFunction={X} height={24} width={24} onClick={() => setShowLogin(false)} />}
                        </h1>
                    }
                    {loginIsProvider
                        ? <h3 className="margin-0 padding-0 color-gray-500 text-xl-regular">
                            Válassz a regisztrációs lehetőségek közül
                        </h3>
                        : <h3 className="padding-0 margin-0 color-gray-500 text-xl-regular">
                            Válaszd ki a regisztrációs lehetőséget
                        </h3>
                    }
                </div>
                <form className={`${classes["form-container"]} ${loginIsProvider ? classes["provider"] : ""}`} onSubmit={e => handleContinue(e)}>
                    <SocialButtons redirectURL={redirectURL} />
                    {loginIsProvider && <button className={`${social_classes["button"]}`} onClick={() => provLoginRegFn()} type="button">
                        <CustomSVG SvgFunction={Mail} height={24} width={24} />
                        <p className="padding-0 margin-0 color-gray-700 text-md-semibold">
                            Folytatás Email-el
                        </p>
                    </button>}
                    {!loginIsProvider && <>
                        <div className="d-flex flex-row align-self-stretch align-items-center gap-6">
                            <div className="divider-200" />
                            <p className="margin-0 padding-0 color-gray-700 text-sm-regular">vagy</p>
                            <div className="divider-200" />
                        </div>
                        {errCode !== 0 && <div className={`${classes["used-email"]}`}>
                            <CustomSVG SvgFunction={AlertTriangle} className={`stroke-warning-500 flex-shrink-0`} height={24} width={24} />
                            <div className="d-flex flex-column align-self-stretch">
                                <p className="margin-0 padding-0 color-gray-700 text-xs-regular">
                                    Regisztráció sikertelen
                                </p>
                                <p className="margin-0 padding-0 color-gray-800 text-xs-medium">
                                    {err_msg}
                                </p>
                                {errCode === 1 && <div>
                                    <CustomButton size="sm" hierarchy="link-gray" type="button" icon="trailing" is_svg={true} svg={ChevronRight} onClick={loginRedirect} >
                                        Bejelentkezés
                                    </CustomButton>
                                </div>}
                                {errCode === 2 && <div>
                                    <CustomButton size="sm" hierarchy="link-gray" type="button" onClick={resendEmail} isLoading={isResending} loadingText="Küldés..." disabled={isResending} >
                                        Újra küldés
                                    </CustomButton>
                                </div>}
                            </div>
                        </div>}
                        <div className="d-flex flex-column align-self-stretch gap-2">
                            <TextInput className="w-100" type="email" value={email} onChange={e => handleEmailChange(e.target.value)} label="E-mail" hint={isError ? isError : undefined} error={isError} placeholder={"e-mail@mail.hu"} required_mark required />
                            {expanded && <PasswordReset ref={passwordRef} registration />}
                            {expanded && <div className={`${classes["checkbox-container"]}`}>
                                {/*removed temporarily - <FormControl id="remember-me" theme="primary" checked={rememberMe} onChange={() => setRememberMe(!rememberMe)} type="checkbox" size={"sm"}
                                    main_text={"Emlékezz rám"}
                                    sub_text={"A következő 30 napig megőrizzük a bejelentkezésedet"}
                                />*/}
                                <FormControl id="aszf" theme="primary" checked={aszf} onChange={() => setAszf(!aszf)} type="checkbox" size={"sm"}
                                    main_text={<p className="margin-0 padding-0 color-gray-700 text-sm-medium">
                                        Elfogadom az <Link href="/adatvedelmi_tajekoztato" rel="noopener noreferrer" target="_blank" className="margin-0 padding-0 color-primary-500 text-sm-medium">Adatkezelési szabályzatot</Link>
                                    </p>}
                                />
                            </div>}
                        </div>
                        <CustomButton className="w-100" size="lg" hierarchy="primary" icon="trailing" is_svg={true} svg={ArrowRight} disabled={(expanded && !aszf) || isSignUpPending} isLoading={isSignUpPending} loadingText="Folyamatban..." >
                            Tovább
                        </CustomButton>
                        <div className="color-gray-700 text-sm-regular d-flex align-self-stretch justify-content-center gap-1 flex-wrap">
                            <p className="margin-0 padding-0 text-center">Már rendelkezel fiókkal?</p>
                            {is_modal
                                ? <span className="margin-0 padding-0 color-gray-700 text-sm-semibold pointer" onClick={() => setShowLoginState("login")}>Bejelentkezés</span>
                                : <LinkWithParams href="/bejelentkezes" className="margin-0 padding-0 color-gray-700 text-sm-semibold">Bejelentkezés</LinkWithParams>
                            }
                        </div>
                    </>}
                    {!is_modal && <><div className="d-flex flex-row align-self-stretch justify-content-center">
                        <div className="divider-200" style={{ width: 128 }} />
                    </div>
                        <div className="d-flex flex-row align-self-stretch justify-content-center">
                            <CustomButton size="md" hierarchy="tertiary-gray" icon="trailing" is_svg={true} svg={ArrowRight} type="button" href={"/szolgaltato/regisztracio"} >
                                Szolgáltatóként regisztrálok
                            </CustomButton>
                        </div></>}
                </form>
            </div>
        }
        
        {/* Email Verification Modal */}
        <EmailVerificationModal
            isOpen={showVerificationModal}
            onClose={() => setShowVerificationModal(false)}
            email={email}
            score={verificationScore}
            onResend={resendEmail}
            isResending={isResending}
        />
        
        {/* reCAPTCHA v2 Modal */}
        <RecaptchaV2Modal
            show={showRecaptchaV2Modal}
            onClose={handleV2Close}
            onSuccess={handleV2Success}
        />
    </div></div>
}

export function RegistrationResendEmail({ email, resendEmail, is_modal, isResending = false }) {
    const { setShowLogin } = useContext(AuthContext);

    return <div className={`${classes["verify-email-container"]} ${is_modal ? "p-0" : ""}`} style={{ margin: "auto" }} >
        <div className="d-flex flex-column flex-start gap-4 align-self-stretch">
            <h1 className={`${classes["registration-container-header-texts-inner"]} color-gray-900 display-md-bold`}>
                Erősítsd meg az e-mail címed
                <SafeImage src={mailbox} width={32} height={32} priority alt="integető kéz" />
                {is_modal && <CustomSVG className={`stroke-gray-500 ${classes["registration-container-x"]}`} SvgFunction={X} height={24} width={24} onClick={() => setShowLogin(false)} />}
            </h1>
            <h3 className="margin-0 padding-0 color-gray-700 text-lg-regular">
                A folytatáshoz elküldtük a linket e-mailben a <span className="text-lg-semibold">{email}</span> címre.<br /><br />
                Ellenőrizd a spam mappát is.
            </h3>
        </div>
        <div className="d-flex flex-row justify-content-center align-items-center align-self-center align-self-stretch gap-1 color-gray-700 text-sm-regular flex-wrap">
            <p className="padding-0 margin-0 text-center">Nem kaptál visszaigazoló e-mailt?</p>
            <CustomButton size="sm" hierarchy="link-gray" type="button" onClick={resendEmail} isLoading={isResending} loadingText="Küldés..." disabled={isResending}>Újraküldöm</CustomButton>
        </div>
    </div>
}